import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  X,
  ChevronRight,
  Home,
  CornerLeftUp,
  FileText,
  ExternalLink,
  WifiOff,
} from "lucide-react";

/**
 * EASA "Easy Access Rules for UAS" (Volltext) für das Web. Lädt das
 * konsolidierte EU-Drohnenregelwerk vom öffentlichen Backend-Endpoint
 * `GET /api/easa-rules`. Spiegelt den mobilen `EasaRulesScreen`. Der amtliche
 * Regeltext ist englisch (EASA-Original); nur die Bedienoberfläche ist deutsch.
 *
 * Abgegrenzt von `/easa` (EasaReference): Das ist eine kuratierte deutsche
 * Zusammenfassung, dies hier ist das durchsuchbare Originalregelwerk.
 */

type EasaNodeKind = "topic" | "heading";
type EasaNodeType = "IR" | "DR" | "AMC" | "GM" | "EAR" | "OTHER";

interface EasaNode {
  id: string;
  kind: EasaNodeKind;
  title: string;
  type?: EasaNodeType;
  source?: string;
  applicabilityDate?: string;
  code?: string;
  depth: number;
  parentId: string | null;
  body?: string[];
}

interface EasaRulesDoc {
  title: string;
  revision: string;
  source: string;
  sourceUrl: string;
  generatedAt: string;
  nodes: EasaNode[];
}

const TYPE_LABELS: Record<EasaNodeType, string> = {
  IR: "Durchführungsverordnung",
  DR: "Delegierte Verordnung",
  AMC: "Annehmbare Nachweisverfahren (AMC)",
  GM: "Anleitungsmaterial (GM)",
  EAR: "Easy Access Rules",
  OTHER: "Sonstiges",
};

const TYPE_COLORS: Record<EasaNodeType, string> = {
  IR: "#0A4D8C",
  DR: "#2D6A8C",
  AMC: "#8C5A0A",
  GM: "#2D8659",
  EAR: "#6B4E9E",
  OTHER: "#5A6472",
};

function typeColor(type?: EasaNodeType): string {
  return type ? TYPE_COLORS[type] ?? TYPE_COLORS.OTHER : TYPE_COLORS.OTHER;
}

function childrenOf(nodes: EasaNode[], parentId: string | null): EasaNode[] {
  return nodes.filter(
    (n) => (n.parentId ?? null) === parentId && n.title.trim().length > 0,
  );
}

function searchTopics(nodes: EasaNode[], query: string): EasaNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return nodes.filter((n) => {
    if (n.kind !== "topic") return false;
    if (n.title.toLowerCase().includes(q)) return true;
    if (n.code && n.code.toLowerCase().includes(q)) return true;
    if (n.body && n.body.some((p) => p.toLowerCase().includes(q))) return true;
    return false;
  });
}

function TypeBadge({ type }: { type?: EasaNodeType }) {
  if (!type) return null;
  const color = typeColor(type);
  return (
    <span
      className="rounded-md px-1.5 py-0.5 text-[10px] font-extrabold"
      style={{ color, backgroundColor: color + "1A" }}
    >
      {type}
    </span>
  );
}

export default function EasaRules() {
  const [search, setSearch] = useState("");
  const [path, setPath] = useState<EasaNode[]>([]);
  const [selected, setSelected] = useState<EasaNode | null>(null);

  const { data, isLoading, isError, refetch } = useQuery<EasaRulesDoc>({
    queryKey: ["/api/easa-rules"],
    staleTime: Infinity,
  });

  const nodes = data?.nodes ?? [];
  const currentParentId = path.length > 0 ? path[path.length - 1].id : null;
  const isSearching = search.trim().length > 0;

  const browseItems = useMemo(
    () => childrenOf(nodes, currentParentId),
    [nodes, currentParentId],
  );
  const searchResults = useMemo(
    () => (isSearching ? searchTopics(nodes, search).slice(0, 200) : []),
    [nodes, search, isSearching],
  );

  const openNode = (node: EasaNode) => {
    if (node.kind === "heading") setPath((p) => [...p, node]);
    else setSelected(node);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-[var(--color-app-text-secondary)]">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-300 border-t-brand-600" />
        <span className="text-sm">Regelwerk wird geladen…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <WifiOff size={32} className="text-[var(--color-app-text-tertiary)]" />
        <p className="max-w-sm text-sm text-[var(--color-app-text-secondary)]">
          Das Regelwerk konnte nicht geladen werden. Bitte später erneut versuchen.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-[10px] bg-brand-600 px-4 py-2 text-sm font-semibold text-white"
          data-testid="button-easa-retry"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  const items = isSearching ? searchResults : browseItems;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-xl border border-[var(--color-app-border)] bg-[var(--color-app-bg)] px-3 py-2.5">
        <Search size={16} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Regelwerk durchsuchen…"
          className="flex-1 bg-transparent text-sm text-[var(--color-app-text)] outline-none placeholder:text-[var(--color-app-text-tertiary)]"
          data-testid="input-easa-search"
        />
        {search.length > 0 ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="shrink-0 text-[var(--color-app-text-tertiary)] hover:text-[var(--color-app-text)]"
            data-testid="button-easa-clear-search"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <p className="text-center text-xs leading-relaxed text-[var(--color-app-text-tertiary)]">
        {data.title} — Revision {data.revision}. Amtlicher Text in englischer Sprache (EASA).
      </p>

      {!isSearching && path.length > 0 ? (
        <div className="flex items-center gap-2 text-sm">
          <button
            type="button"
            onClick={() => setPath([])}
            className="text-brand-600 hover:text-brand-700"
            data-testid="button-easa-breadcrumb-root"
          >
            <Home size={15} />
          </button>
          <ChevronRight size={13} className="text-[var(--color-app-text-tertiary)]" />
          <button
            type="button"
            onClick={() => setPath((p) => p.slice(0, -1))}
            className="flex min-w-0 items-center gap-1.5 font-semibold text-brand-600 hover:text-brand-700"
            data-testid="button-easa-breadcrumb-up"
          >
            <CornerLeftUp size={14} className="shrink-0" />
            <span className="truncate">{path[path.length - 1].title}</span>
          </button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-[var(--color-app-text-secondary)]">
          <Search size={32} className="text-[var(--color-app-text-tertiary)]" />
          <span className="text-sm">
            {isSearching ? "Kein Treffer gefunden" : "Keine Einträge"}
          </span>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isHeading = item.kind === "heading";
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => openNode(item)}
                className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-app-border)] bg-[var(--color-app-bg)] p-3 text-left transition hover:bg-[var(--color-app-bg-secondary)]"
                data-testid={`easa-item-${item.id}`}
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {item.code ? (
                      <span className="shrink-0 text-xs font-bold text-brand-600">
                        {item.code}
                      </span>
                    ) : null}
                    {!isHeading ? <TypeBadge type={item.type} /> : null}
                  </div>
                  <div
                    className={`text-sm leading-snug text-[var(--color-app-text)] ${
                      isHeading ? "font-bold" : "font-semibold"
                    }`}
                  >
                    {item.title}
                  </div>
                  {item.source ? (
                    <div className="truncate text-[11px] text-[var(--color-app-text-tertiary)]">
                      {item.source}
                    </div>
                  ) : null}
                </div>
                {isHeading ? (
                  <ChevronRight size={18} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
                ) : (
                  <FileText size={18} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {selected ? (
        <DetailModal
          node={selected}
          sourceUrl={data.sourceUrl}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}

function DetailModal({
  node,
  sourceUrl,
  onClose,
}: {
  node: EasaNode;
  sourceUrl: string;
  onClose: () => void;
}) {
  const tLabel = node.type ? TYPE_LABELS[node.type] : null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      data-testid="easa-detail-backdrop"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-[var(--color-app-bg)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--color-app-border)] p-5">
          <h2 className="flex-1 text-lg font-bold leading-snug text-[var(--color-app-text)]">
            {node.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-[var(--color-app-text-secondary)] hover:text-[var(--color-app-text)]"
            data-testid="button-easa-detail-close"
          >
            <X size={24} />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="flex flex-wrap items-center gap-2">
            {node.code ? (
              <span className="rounded-lg bg-brand-100 px-2.5 py-1 text-xs font-bold text-brand-600">
                {node.code}
              </span>
            ) : null}
            {node.type ? (
              <span
                className="rounded-lg px-2.5 py-1 text-xs font-bold"
                style={{ color: typeColor(node.type), backgroundColor: typeColor(node.type) + "1A" }}
              >
                {node.type}
              </span>
            ) : null}
          </div>

          <div className="mt-3 space-y-1 text-[13px] text-[var(--color-app-text-secondary)]">
            {tLabel ? <p>{tLabel}</p> : null}
            {node.source ? <p>Quelle: {node.source}</p> : null}
            {node.applicabilityDate ? <p>Anwendbar ab: {node.applicabilityDate}</p> : null}
          </div>

          <div className="my-4 h-px bg-[var(--color-app-border)]" />

          {(node.body ?? []).length > 0 ? (
            <div className="space-y-3">
              {(node.body ?? []).map((p, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-[var(--color-app-text)]">
                  {p}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-[var(--color-app-text-tertiary)]">
              Für diesen Abschnitt liegt kein Fließtext vor.
            </p>
          )}

          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="mt-4 inline-flex items-center gap-2 rounded-[10px] border border-[var(--color-app-border)] px-3 py-2 text-[13px] font-semibold text-brand-600 hover:border-brand-300 hover:bg-brand-50"
            data-testid="button-easa-source-link"
          >
            <ExternalLink size={14} />
            EASA Easy Access Rules (Originaldokument)
          </a>
        </div>
      </div>
    </div>
  );
}
